import { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDocs, writeBatch, serverTimestamp, Timestamp } from "firebase/firestore";
import Papa from "papaparse";
import { FiUpload, FiX, FiCheckCircle, FiAlertTriangle, FiTrash2, FiCopy, FiSlash, FiRotateCcw } from "react-icons/fi";
import { db } from "../firebase";

const COLLECTION_NAME = "betaSignups";
const BATCH_SIZE = 400; // Firestore batched writes cap at 500 ops
const DEFAULT_PLATFORM = "android"; // only platform open for beta right now

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString() : "Unknown";
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TRUTHY_RE = /^(y|yes|true|1)$/i;

// ── CSV import field mapping ─────────────────────────────────────
const IMPORT_FIELDS = [
  { key: "email", label: "Email", required: true, synonyms: ["email", "e-mail", "mail", "email address"] },
  { key: "name", label: "Name", required: false, synonyms: ["name", "full name", "fullname"] },
  { key: "platform", label: "Platform", required: false, synonyms: ["platform", "os", "device"] },
  { key: "willingToGiveFeedback", label: "Feedback OK?", required: false, synonyms: ["feedback", "willing to give feedback", "feedback ok", "give feedback"] },
  { key: "excitedFeature", label: "Excited Feature", required: false, synonyms: ["excited feature", "feature", "excited about", "most excited"] },
  { key: "createdAt", label: "Signed up date (optional)", required: false, synonyms: ["date", "timestamp", "created", "createdat", "joined", "submitted", "signup date", "signed up"] },
];

const guessMapping = (headers) => {
  const mapping = {};
  const normalized = headers.map((h) => ({ raw: h, norm: h.trim().toLowerCase() }));
  IMPORT_FIELDS.forEach((field) => {
    const match = normalized.find((h) => field.synonyms.includes(h.norm));
    mapping[field.key] = match ? match.raw : "";
  });
  return mapping;
};

const fieldStyle = {
  fontFamily: "'Quicksand', sans-serif", fontSize: 13, fontWeight: 600,
  padding: "7px 10px", borderRadius: 8,
  border: "1.5px solid #E0E0E0", background: "#fff",
  outline: "none", color: "#000",
};

// ── Import panel ──────────────────────────────────────────────────
function ImportPanel({ onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (file) => {
    if (!file) return;
    setParseError("");
    setResult(null);
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const fields = results.meta.fields || [];
        if (fields.length === 0 || results.data.length === 0) {
          setParseError("Couldn't find any rows in that file.");
          setHeaders([]);
          setRows([]);
          return;
        }
        setHeaders(fields);
        setRows(results.data);
        setMapping(guessMapping(fields));
      },
      error: (err) => {
        setParseError(`Couldn't parse that CSV: ${err.message}`);
      },
    });
  };

  const preview = useMemo(() => rows.slice(0, 5), [rows]);

  const canImport = !!mapping.email && rows.length > 0 && !importing;

  const handleImport = async () => {
    if (!mapping.email) return;
    setImporting(true);
    setResult(null);

    const seen = new Map(); // lowercased email -> normalized row
    let invalid = 0;
    let duplicates = 0;

    rows.forEach((row) => {
      const rawEmail = (row[mapping.email] || "").trim().toLowerCase();
      if (!EMAIL_RE.test(rawEmail)) {
        invalid += 1;
        return;
      }
      if (seen.has(rawEmail)) duplicates += 1;

      const name = mapping.name ? (row[mapping.name] || "").trim() : "";
      const platform = (mapping.platform ? (row[mapping.platform] || "").trim().toLowerCase() : "") || DEFAULT_PLATFORM;
      const willingToGiveFeedback = mapping.willingToGiveFeedback
        ? TRUTHY_RE.test((row[mapping.willingToGiveFeedback] || "").trim())
        : false;
      const excitedFeature = mapping.excitedFeature ? (row[mapping.excitedFeature] || "").trim() : "";
      const rawDate = mapping.createdAt ? row[mapping.createdAt] : "";
      const parsedDate = rawDate ? toDate(rawDate) : null;

      seen.set(rawEmail, {
        email: rawEmail,
        name,
        platform,
        willingToGiveFeedback,
        excitedFeature,
        createdAt: parsedDate ? Timestamp.fromDate(parsedDate) : serverTimestamp(),
      });
    });

    const toWrite = Array.from(seen.values());

    try {
      for (let i = 0; i < toWrite.length; i += BATCH_SIZE) {
        const chunk = toWrite.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((item) => {
          const ref = doc(db, COLLECTION_NAME, item.email);
          batch.set(ref, {
            name: item.name,
            email: item.email,
            platform: item.platform,
            willingToGiveFeedback: item.willingToGiveFeedback,
            excitedFeature: item.excitedFeature,
            createdAt: item.createdAt,
          }, { merge: true });
        });
        await batch.commit();
      }

      setResult({ imported: toWrite.length, invalid, duplicates, error: null });
      onImported();
    } catch (err) {
      const message = err?.code === "permission-denied"
        ? "Permission denied. Your CMS admin account needs a Firestore rule allowing writes to the betaSignups collection (allow create, update: if isAdmin();)."
        : `Import failed: ${err.message}`;
      setResult({ imported: 0, invalid, duplicates, error: message });
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFileName("");
    setHeaders([]);
    setRows([]);
    setMapping({});
    setParseError("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div style={{
      background: "#f7f9fb", borderRadius: 16,
      border: "1.5px solid #E0E0E0",
      padding: "18px 20px", marginBottom: 20,
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2a9d8f" }}>
          Import Beta Signups from CSV
        </div>
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#606060", display: "flex" }}
          title="Close"
        >
          <FiX size={16} />
        </button>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: "#606060" }}>
        Export your spreadsheet of existing beta signups as CSV, then upload it here. Any row without "Platform" mapped defaults to "{DEFAULT_PLATFORM}".
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="primary-button"
          style={{
            backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80",
            fontSize: 13, padding: "8px 16px",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <FiUpload size={13} /> Choose CSV file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => handleFile(e.target.files?.[0])}
          style={{ display: "none" }}
        />
        {fileName && <span style={{ fontSize: 13, color: "#606060", fontWeight: 600 }}>{fileName}</span>}
        {(headers.length > 0 || result) && (
          <button
            type="button"
            onClick={reset}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#e76f51", fontWeight: 700 }}
          >
            Clear
          </button>
        )}
      </div>

      {parseError && (
        <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          <FiAlertTriangle size={14} /> {parseError}
        </div>
      )}

      {headers.length > 0 && !result && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {IMPORT_FIELDS.map((field) => (
              <label key={field.key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700, color: "#606060" }}>
                {field.label}{field.required && " *"}
                <select
                  value={mapping[field.key] || ""}
                  onChange={(e) => setMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  style={fieldStyle}
                >
                  <option value="">— none —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Platform</th>
                  <th>Feedback OK?</th>
                  <th>Excited Feature</th>
                  <th>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    <td>{mapping.name ? row[mapping.name] : "—"}</td>
                    <td>{mapping.email ? row[mapping.email] : "—"}</td>
                    <td>{mapping.platform ? row[mapping.platform] : DEFAULT_PLATFORM}</td>
                    <td>{mapping.willingToGiveFeedback ? row[mapping.willingToGiveFeedback] : "—"}</td>
                    <td>{mapping.excitedFeature ? row[mapping.excitedFeature] : "—"}</td>
                    <td>{mapping.createdAt ? row[mapping.createdAt] : "Import time"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: "#aaa" }}>
            Previewing {preview.length} of {rows.length} row{rows.length !== 1 ? "s" : ""}. Rows without a valid email are skipped; duplicate emails keep the last occurrence.
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleImport}
              disabled={!canImport}
              className="primary-button"
              style={{
                backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80",
                fontSize: 13, padding: "8px 18px",
                opacity: canImport ? 1 : 0.5,
                cursor: canImport ? "pointer" : "not-allowed",
              }}
            >
              {importing ? "Importing…" : `Import ${rows.length} row${rows.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </>
      )}

      {result && (
        <div style={{
          borderRadius: 12, padding: "12px 16px",
          background: result.error ? "#fdecea" : "#eafaf3",
          border: `1.5px solid ${result.error ? "#e76f51" : "#2a9d8f"}`,
          display: "flex", alignItems: "flex-start", gap: 10,
        }}>
          {result.error ? <FiAlertTriangle size={16} color="#e76f51" style={{ flexShrink: 0, marginTop: 2 }} /> : <FiCheckCircle size={16} color="#2a9d8f" style={{ flexShrink: 0, marginTop: 2 }} />}
          <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
            {result.error ? (
              result.error
            ) : (
              <>
                Imported {result.imported} signup{result.imported !== 1 ? "s" : ""}.
                {result.duplicates > 0 && ` ${result.duplicates} duplicate email${result.duplicates !== 1 ? "s" : ""} in the file (kept last).`}
                {result.invalid > 0 && ` ${result.invalid} row${result.invalid !== 1 ? "s" : ""} skipped (invalid email).`}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Falls back to a manual-copy prompt if the Clipboard API is unavailable or
// denied (e.g. non-secure context), so the action never silently fails.
const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to manual prompt
    }
  }
  window.prompt("Copy failed automatically — copy this manually:", text);
  return false;
};

function BetaSignups() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [updatingOptOut, setUpdatingOptOut] = useState(false);
  const [optOutError, setOptOutError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  const fetchEntries = async () => {
    try {
      const snap = await getDocs(collection(db, COLLECTION_NAME));
      const items = snap.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        return {
          id: docSnap.id,
          name: data.name || "",
          email: data.email || docSnap.id,
          platform: data.platform || "",
          willingToGiveFeedback: !!data.willingToGiveFeedback,
          excitedFeature: data.excitedFeature || "",
          createdAt: data.createdAt ?? null,
          emailOptOut: !!data.emailOptOut,
        };
      });

      items.sort((a, b) => {
        const aDate = toDate(a.createdAt)?.getTime() || 0;
        const bDate = toDate(b.createdAt)?.getTime() || 0;
        return bDate - aDate;
      });

      setEntries(items);
      setError("");
    } catch (err) {
      setError("Unable to load beta signups. You may need Firestore read access — see the security rules note.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((item) =>
      item.name.toLowerCase().includes(q) ||
      item.email.toLowerCase().includes(q) ||
      item.excitedFeature.toLowerCase().includes(q)
    );
  }, [entries, search]);

  const filteredIds = useMemo(() => filtered.map((item) => item.id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...filteredIds]);
    });
  };

  const deleteIds = async (ids) => {
    setDeleting(true);
    setDeleteError("");
    try {
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const chunk = ids.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((id) => batch.delete(doc(db, COLLECTION_NAME, id)));
        await batch.commit();
      }
      const removed = new Set(ids);
      setEntries((prev) => prev.filter((item) => !removed.has(item.id)));
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    } catch (err) {
      const message = err?.code === "permission-denied"
        ? "Permission denied. Your CMS admin account needs a Firestore rule allowing deletes on the betaSignups collection (allow delete: if isAdmin();)."
        : `Delete failed: ${err.message}`;
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteOne = (item) => {
    if (!window.confirm(`Remove ${item.email} from beta signups?`)) return;
    deleteIds([item.id]);
  };

  const handleDeleteSelected = () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} beta signup${selected.size !== 1 ? "s" : ""}?`)) return;
    deleteIds(Array.from(selected));
  };

  // Toggles emailOptOut on Firestore docs without touching any other field,
  // so opted-out signups stay in the "betaSignups" collection — they're
  // just excluded from bulk-send recipient lists below.
  const setOptOut = async (ids, optOut) => {
    if (ids.length === 0) return;
    setUpdatingOptOut(true);
    setOptOutError("");
    try {
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const chunk = ids.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((id) => batch.set(doc(db, COLLECTION_NAME, id), { emailOptOut: optOut }, { merge: true }));
        await batch.commit();
      }
      const changed = new Set(ids);
      setEntries((prev) => prev.map((item) => (changed.has(item.id) ? { ...item, emailOptOut: optOut } : item)));
    } catch (err) {
      const message = err?.code === "permission-denied"
        ? "Permission denied. Your CMS admin account needs a Firestore rule allowing updates on the betaSignups collection (allow update: if isAdmin();)."
        : `Update failed: ${err.message}`;
      setOptOutError(message);
    } finally {
      setUpdatingOptOut(false);
    }
  };

  const handleToggleOptOut = (item) => setOptOut([item.id], !item.emailOptOut);

  const handleRemoveSelectedFromEmailList = () => {
    if (selected.size === 0) return;
    setOptOut(Array.from(selected), true);
  };

  const filteredRecipients = useMemo(
    () => filtered.filter((item) => !item.emailOptOut).map((item) => item.email).filter(Boolean),
    [filtered]
  );

  const selectedEntries = useMemo(() => entries.filter((item) => selected.has(item.id)), [entries, selected]);

  const selectedRecipients = useMemo(
    () => selectedEntries.filter((item) => !item.emailOptOut).map((item) => item.email).filter(Boolean),
    [selectedEntries]
  );

  const handleCopyEmails = async (emails) => {
    if (emails.length === 0) return;
    setCopyStatus("");
    const ok = await copyToClipboard(emails.join(", "));
    setCopyStatus(ok ? `Copied ${emails.length} email${emails.length !== 1 ? "s" : ""} to clipboard — paste into Gmail's Bcc field.` : "");
  };

  return (
    <div className="page">
      <h1>Beta Signups</h1>
      <p style={{ color: "#666" }}>
        Showing data from the "{COLLECTION_NAME}" collection · {entries.length} signup{entries.length !== 1 ? "s" : ""}
      </p>

      {loading && <p>Loading beta signups...</p>}
      {error && <p style={{ color: "#e76f51" }}>{error}</p>}

      {!loading && !error && (
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0 }}>All Signups</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Search name, email, or excited feature…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600,
                  padding: "8px 14px", borderRadius: 10,
                  border: "1.5px solid #E0E0E0", background: "#f7f9fb",
                  outline: "none", color: "#000", minWidth: 240,
                }}
              />
              {selected.size > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => handleCopyEmails(selectedRecipients)}
                    disabled={selectedRecipients.length === 0}
                    className="primary-button"
                    style={{
                      backgroundColor: "#fff", color: "#2a9d8f", borderColor: "#258D80",
                      fontSize: 13, padding: "8px 16px",
                      display: "inline-flex", alignItems: "center", gap: 6,
                      opacity: selectedRecipients.length === 0 ? 0.5 : 1,
                    }}
                    title="Copy selected signups' emails, comma-separated, to paste into Gmail's Bcc field"
                  >
                    <FiCopy size={13} /> Copy Selected ({selectedRecipients.length})
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveSelectedFromEmailList}
                    disabled={updatingOptOut}
                    className="primary-button"
                    style={{
                      backgroundColor: "#fff", color: "#e08a00", borderColor: "#e08a00",
                      fontSize: 13, padding: "8px 16px",
                      display: "inline-flex", alignItems: "center", gap: 6,
                      opacity: updatingOptOut ? 0.6 : 1,
                    }}
                    title="Keep these people as beta signups, but stop sending them bulk email"
                  >
                    <FiSlash size={13} /> {updatingOptOut ? "Updating…" : `Remove ${selected.size} from Email List`}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={deleting}
                    className="primary-button"
                    style={{
                      backgroundColor: "#e76f51", color: "#fff", borderColor: "#cf6348",
                      fontSize: 13, padding: "8px 16px",
                      display: "inline-flex", alignItems: "center", gap: 6,
                      opacity: deleting ? 0.6 : 1,
                    }}
                    title="Delete these people from beta signups entirely"
                  >
                    <FiTrash2 size={13} /> {deleting ? "Removing…" : `Delete ${selected.size} Selected`}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => handleCopyEmails(filteredRecipients)}
                disabled={filteredRecipients.length === 0}
                className="primary-button"
                style={{
                  backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80",
                  fontSize: 13, padding: "8px 16px",
                  display: "inline-flex", alignItems: "center", gap: 6,
                  opacity: filteredRecipients.length === 0 ? 0.5 : 1,
                }}
                title="Copy everyone below's emails (except those removed from the email list), comma-separated, to paste into Gmail's Bcc field"
              >
                <FiCopy size={13} /> Copy {search.trim() ? "Filtered" : "All"} Emails ({filteredRecipients.length})
              </button>
              <button
                type="button"
                onClick={() => setShowImport((v) => !v)}
                className="primary-button"
                style={{
                  backgroundColor: showImport ? "#fff" : "#2a9d8f",
                  color: showImport ? "#2a9d8f" : "#fff",
                  borderColor: "#258D80",
                  fontSize: 13, padding: "8px 16px",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <FiUpload size={13} /> Import CSV
              </button>
            </div>
          </div>

          {deleteError && (
            <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <FiAlertTriangle size={14} /> {deleteError}
            </div>
          )}

          {optOutError && (
            <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <FiAlertTriangle size={14} /> {optOutError}
            </div>
          )}

          {copyStatus && (
            <div style={{
              borderRadius: 12, padding: "10px 16px", marginBottom: 12,
              background: "#eafaf3", border: "1.5px solid #2a9d8f",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#333", display: "flex", alignItems: "center", gap: 8 }}>
                <FiCheckCircle size={16} color="#2a9d8f" /> {copyStatus}
              </span>
              <button
                onClick={() => setCopyStatus("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#606060", display: "flex" }}
                title="Dismiss"
              >
                <FiX size={14} />
              </button>
            </div>
          )}

          {showImport && (
            <ImportPanel
              onClose={() => setShowImport(false)}
              onImported={fetchEntries}
            />
          )}

          {filtered.length === 0 ? (
            <p>{entries.length === 0 ? "No signups yet." : "No signups match your search."}</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleAllFiltered}
                        aria-label="Select all"
                      />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Feedback OK?</th>
                    <th>Excited Feature</th>
                    <th>Platform</th>
                    <th>Signed up</th>
                    <th>Email Opt-out</th>
                    <th style={{ width: 68 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(item.id)}
                          onChange={() => toggleOne(item.id)}
                          aria-label={`Select ${item.email}`}
                        />
                      </td>
                      <td>{item.name || "Unknown"}</td>
                      <td>{item.email}</td>
                      <td>{item.willingToGiveFeedback ? "Yes" : "No"}</td>
                      <td>{item.excitedFeature || "—"}</td>
                      <td>{item.platform || "—"}</td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
                          color: item.emailOptOut ? "#e08a00" : "#2a9d8f",
                          background: item.emailOptOut ? "#fff4e0" : "#eafaf3",
                        }}>
                          {item.emailOptOut ? "Removed" : "Subscribed"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <button
                            type="button"
                            onClick={() => handleToggleOptOut(item)}
                            disabled={updatingOptOut}
                            title={item.emailOptOut ? "Add back to email list" : "Remove from email list (keeps them as a beta signup)"}
                            style={{
                              background: "none", border: "none", cursor: updatingOptOut ? "not-allowed" : "pointer",
                              color: item.emailOptOut ? "#2a9d8f" : "#e08a00", display: "flex", alignItems: "center", padding: 4,
                              opacity: updatingOptOut ? 0.5 : 1,
                            }}
                          >
                            {item.emailOptOut ? <FiRotateCcw size={14} /> : <FiSlash size={14} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteOne(item)}
                            disabled={deleting}
                            title="Delete from beta signups"
                            style={{
                              background: "none", border: "none", cursor: deleting ? "not-allowed" : "pointer",
                              color: "#e76f51", display: "flex", alignItems: "center", padding: 4,
                              opacity: deleting ? 0.5 : 1,
                            }}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BetaSignups;
